import type { APIResponse } from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'

interface LoginPayload {
    email: string
    password: string
}

interface RegisterPayload {
    email: string
    password: string
    name: string
    tenantName: string
}

interface AuthResponse {
    token: string
    user: {
        id: string
        email: string
        name: string
        isSuperAdmin?: boolean
    }
    tenant: {
        id: string
        name?: string
        slug?: string
        role?: string
    } | null
    isNewUser?: boolean
}

interface GoogleAuthPayload {
    idToken: string
}

/** Password / Google proved; the account has MFA on and still owes a code. */
export interface MfaChallenge {
    requiresMFA: true
    mfaChallengeToken: string
}

export type SignInResult = AuthResponse | MfaChallenge

export const needsMfa = (r: SignInResult): r is MfaChallenge =>
    (r as MfaChallenge).requiresMFA === true

export interface MfaSetup {
    secret: string
    uri: string
    recoveryCodes: string[]
}

export const authApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        login: builder.mutation<SignInResult, LoginPayload>({
            query: body => ({
                url: '/auth/login',
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<SignInResult>) => res.data!,
        }),

        mfaVerify: builder.mutation<AuthResponse, { challengeToken: string; code: string }>({
            query: body => ({ url: '/auth/mfa/verify', method: 'POST', body }),
            transformResponse: (res: APIResponse<AuthResponse>) => res.data!,
        }),

        mfaRecovery: builder.mutation<
            AuthResponse,
            { challengeToken: string; recoveryCode: string }
        >({
            query: body => ({ url: '/auth/mfa/recovery', method: 'POST', body }),
            transformResponse: (res: APIResponse<AuthResponse>) => res.data!,
        }),

        mfaSetup: builder.mutation<MfaSetup, void>({
            query: () => ({ url: '/auth/mfa/setup', method: 'POST' }),
            transformResponse: (res: APIResponse<MfaSetup>) => res.data!,
        }),

        mfaVerifySetup: builder.mutation<void, { code: string }>({
            query: body => ({ url: '/auth/mfa/verify-setup', method: 'POST', body }),
        }),

        mfaRegenerateCodes: builder.mutation<{ recoveryCodes: string[] }, { code: string }>({
            query: body => ({ url: '/auth/mfa/recovery-codes', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ recoveryCodes: string[] }>) => res.data!,
        }),

        mfaDisable: builder.mutation<void, { password: string }>({
            query: body => ({ url: '/auth/mfa/disable', method: 'POST', body }),
        }),

        register: builder.mutation<AuthResponse, RegisterPayload>({
            query: body => ({
                url: '/auth/register',
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<AuthResponse>) => res.data!,
        }),

        googleLogin: builder.mutation<SignInResult, GoogleAuthPayload>({
            query: body => ({
                url: '/auth/google',
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<SignInResult>) => res.data!,
        }),
    }),
})

export const {
    useLoginMutation,
    useRegisterMutation,
    useGoogleLoginMutation,
    useMfaVerifyMutation,
    useMfaRecoveryMutation,
    useMfaSetupMutation,
    useMfaVerifySetupMutation,
    useMfaDisableMutation,
    useMfaRegenerateCodesMutation,
} = authApi
